import React, { useState, useEffect, useCallback } from "react";

const UNIT_OPTIONS = [
  { id: "years", label: "Years" },
  { id: "months", label: "Months" },
];

function toYears(rawValue, unit) {
  const n = Number(rawValue);
  if (!Number.isFinite(n) || n <= 0) return null;
  return unit === "months" ? n / 12 : n;
}

function fromYears(years, unit) {
  if (years == null || !Number.isFinite(Number(years))) return "";
  const y = Number(years);
  if (unit === "months") return Math.round(y * 12);
  return Number.isInteger(y) ? y : Number(y.toFixed(2));
}

/**
 * Numeric horizon input + Years/Months unit toggle. Normalizes internally
 * to `horizon_years` (a free positive float) — NEVER restricts a horizon
 * to a fixed 1/3/5/10yr set (P0 Company Trajectory architecture redesign,
 * 2026-08). Used by both the Target Editor and Growth Mentor's plan form
 * so a founder can express "18 months" or "7 years" identically anywhere
 * a horizon is entered.
 *
 * Controlled: `horizonYears` is the source of truth; `onChange(years)`
 * fires with the normalized float whenever the number or the unit changes.
 */
export default function HorizonInput({ horizonYears, onChange, testIdPrefix = "horizon-input", disabled = false }) {
  const [unit, setUnit] = useState(horizonYears != null && horizonYears < 1 ? "months" : "years");
  const [raw, setRaw] = useState(() => fromYears(horizonYears, unit));

  // Re-sync the visible number when the source of truth changes from
  // OUTSIDE this input (e.g. a canonical target refetch) — never fights
  // the user mid-keystroke since `onChange` always updates the same prop
  // synchronously in the same render pass.
  useEffect(() => {
    setRaw(fromYears(horizonYears, unit));
  }, [horizonYears]); // deliberately excludes `unit` — see comment above

  const emit = useCallback((nextRaw, nextUnit) => {
    const years = toYears(nextRaw, nextUnit);
    if (years != null) onChange(years);
  }, [onChange]);

  const changeUnit = (nextUnit) => {
    const nextRaw = fromYears(horizonYears, nextUnit);
    setUnit(nextUnit);
    setRaw(nextRaw);
    emit(nextRaw, nextUnit);
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min="0"
        step={unit === "months" ? "1" : "0.1"}
        value={raw}
        disabled={disabled}
        onChange={(e) => { setRaw(e.target.value); emit(e.target.value, unit); }}
        data-testid={`${testIdPrefix}-value`}
        className="w-20 rounded-md border border-slate-200 bg-white px-2 py-2 text-[14px] tabular-nums focus:border-blue-500 focus:outline-none disabled:opacity-60"
      />
      <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
        {UNIT_OPTIONS.map((u) => (
          <button
            key={u.id}
            type="button"
            disabled={disabled}
            onClick={() => changeUnit(u.id)}
            data-testid={`${testIdPrefix}-unit-${u.id}`}
            className={`rounded px-2.5 py-1.5 text-[12px] font-semibold ${
              unit === u.id ? "bg-[#0F1B2D] text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {u.label}
          </button>
        ))}
      </div>
    </div>
  );
}
